/**
 * useLgUp — "is the viewport at Tailwind's `lg` or wider?" (CRM UI streamline
 * D-12, phone parity).
 *
 * WHY A HOOK AT ALL. Layout tiers belong in CSS: every pane on this console
 * shows and hides with `hidden` / `lg:block`, never with JavaScript, so a
 * resize is a repaint rather than a re-mount. This exists for the handful of
 * decisions CSS cannot reach — BEHAVIOR that differs by tier. The one today is
 * the Needs-you auto-select: on a laptop the right pane must never open empty,
 * so the list preselects its oldest item; on a phone the two panes are the same
 * column, and preselecting means the recruiter lands on a detail view they
 * never asked for with the list nowhere in sight. No class can express "do not
 * pick a row".
 *
 * FAIL DIRECTION: TRUE. `matchMedia` is missing or throwing means we are in a
 * jsdom test, an old embedded webview, or something equally unusual — and this
 * product is laptop-first, so the unknown case gets the laptop behavior.
 * Failing to `false` would silently strip the auto-select from every desktop in
 * that situation, which is the worse of the two mistakes. (This is the module's
 * one live matchMedia hook — useBoardRowHeight went constant when the board row
 * unified at one height.)
 *
 * The 1024 below is Tailwind's `lg`, the SAME breakpoint the `lg:` classes on
 * the panes it gates use — the two must agree or the pane and the behavior
 * disagree at exactly one viewport width.
 */
import { useEffect, useState } from 'react';

/** Tailwind's `lg` breakpoint, spelled as a media query. */
const LG_QUERY = '(min-width: 1024px)';

export function useLgUp(): boolean {
  const [lgUp, setLgUp] = useState<boolean>(() => {
    try {
      return window.matchMedia(LG_QUERY).matches;
    } catch {
      return true; // laptop-first: assume the wide layout when we cannot ask
    }
  });
  useEffect(() => {
    let mq: MediaQueryList;
    try {
      mq = window.matchMedia(LG_QUERY);
    } catch {
      return undefined;
    }
    const apply = () => setLgUp(mq.matches);
    apply(); // re-sync in case the viewport changed between init and effect
    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', apply);
      return () => mq.removeEventListener('change', apply);
    }
    return undefined;
  }, []);
  return lgUp;
}

export default useLgUp;
